/* =========================================================================
   GIFT REWARDS
   Custom gift-with-purchase engine for Duckiewonderland. Replaces BOGOS.

   Two design constraints drive almost every decision in this file, and both
   come from cart-bundle.js, which owns the cart in this theme:

   1. cart-bundle.js runs a MutationObserver over document.documentElement
      with subtree:true and calls enhanceAll() on every added node. Anything
      that rewrites its own markup on each cart change drags that loop along
      with it. BOGOS did exactly that. So the progress bar is built once and
      afterwards updated by writing to existing text nodes, style custom
      properties and classes — none of which are childList mutations.

   2. cart-bundle.js patches window.fetch. On any POST to /cart/add it fires
      the flying-duck animation and consumes the pending _replace state from
      the bundle "Change" flow. A gift the engine adds by itself must not do
      either, so gift writes go through a fetch captured before that patch is
      installed. DWLCart.add() is unusable here for the same reason plus one
      more: it calls refreshCartUI(true), which force-opens the drawer.

   Everything the engine does still resolves to at most two cart calls per
   change: one /cart/update.js and one /cart/add.js.
   ========================================================================= */
(function () {
  'use strict';

  var configEl = document.getElementById('GiftRewardsConfig');
  if (!configEl) return;

  var CONFIG;
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (err) {
    console.warn('[gift-rewards] configuration could not be read', err);
    return;
  }
  if (!CONFIG.enabled) return;

  var PROP_GIFT = '_gift';
  var PROP_TIER = '_gift_tier';
  var ATTR_ACTIVE = '_gift_active';

  /* Every cart mutation made by anyone other than us, /cart/add included.
     cart-bundle.js calls its own module-private refreshCartUI from add(),
     doBundleRemove() and its fetch patch — not window.DWLCart.refreshCartUI —
     so decorating the exported property never fires for a plain add-to-cart. */
  var FOREIGN_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function attrChoice(id) { return '_gift_choice_' + id; }
  function attrOff(id) { return '_gift_off_' + id; }

  var TIERS = (CONFIG.tiers || [])
    .filter(function (t) {
      return t.active && t.customerOk && t.options && t.options.length;
    })
    .sort(function (a, b) { return a.order - b.order; });

  var SHIPPING = CONFIG.shipping && CONFIG.shipping.enabled && CONFIG.shipping.threshold > 0
    ? CONFIG.shipping
    : null;

  var FILLER = CONFIG.filler && CONFIG.filler.enabled && (CONFIG.filler.products || []).length
    ? CONFIG.filler
    : null;

  if (!TIERS.length && !SHIPPING) return;

  var state = { cart: null, busy: false, queued: false, timer: null, selfRefresh: false };

  function log() {
    if (!CONFIG.debug) return;
    console.log.apply(console, ['[gift-rewards]'].concat([].slice.call(arguments)));
  }

  /* -----------------------------------------------------------------------
     Formatting
     ----------------------------------------------------------------------- */

  function withDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number === null) number = 0;
    var parts = number.toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function formatMoney(cents) {
    var format = CONFIG.moneyFormat || '&euro;{{amount_with_comma_separator}}';
    var value = cents / 100;
    var out = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      switch (key) {
        case 'amount': return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2, '.', ',');
      }
    });
    return out.replace(/&euro;/g, '\u20ac')
              .replace(/&pound;/g, '\u00a3')
              .replace(/&yen;/g, '\u00a5')
              .replace(/&dollar;/g, '$')
              .replace(/&nbsp;/g, '\u00a0')
              .replace(/&amp;/g, '&');
  }

  function interpolate(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* -----------------------------------------------------------------------
     Cart transport
     ----------------------------------------------------------------------- */

  var nativeFetch = window.fetch.bind(window);
  var nativeFetchWasTainted = !!window.DWLCart;

  function reacquireFetch() {
    try {
      var frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      frame.style.cssText = 'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none';
      document.body.appendChild(frame);
      if (frame.contentWindow && frame.contentWindow.fetch) {
        nativeFetch = frame.contentWindow.fetch.bind(frame.contentWindow);
        return true;
      }
    } catch (err) {
      log('could not re-acquire a clean fetch', err);
    }
    return false;
  }

  function postJSON(url, body) {
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().catch(function () { return null; });
    });
  }

  function getCart() {
    return nativeFetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (r) { return r.json(); });
  }

  /* -----------------------------------------------------------------------
     DWLCart bridge
     ----------------------------------------------------------------------- */

  function dwl() {
    return (window.DWLCart && typeof window.DWLCart.refreshCartUI === 'function')
      ? window.DWLCart
      : null;
  }

  function drawerSectionId() {
    var d = dwl();
    if (d && typeof d.drawerSectionId === 'function') {
      try { return d.drawerSectionId(); } catch (err) { /* fall through */ }
    }
    var el = document.querySelector('cart-drawer');
    var wrap = el && el.closest('.shopify-section');
    return wrap && wrap.id ? wrap.id.replace('shopify-section-', '') : null;
  }

  function mainCartSectionId() {
    var mc = document.querySelector('.main-cart');
    var wrap = mc && mc.closest('.shopify-section');
    return wrap && wrap.id ? wrap.id.replace('shopify-section-', '') : null;
  }

  function refreshSectionIds() {
    var ids = [];
    var drawer = drawerSectionId();
    if (drawer) ids.push(drawer);
    var main = mainCartSectionId();
    if (main) ids.push(main);
    return ids;
  }

  function syncCartUI(pre) {
    document.dispatchEvent(new CustomEvent('gift-rewards:updated', {
      detail: { cart: state.cart }
    }));

    var d = dwl();
    if (d) {
      /* false: never force the drawer open. A gift appearing is not a reason
         to interrupt someone who is still shopping. */
      state.selfRefresh = true;
      try {
        return Promise.resolve(d.refreshCartUI(false, pre || null))
          .catch(function () {})
          .then(function () {
            state.selfRefresh = false;
            /* refreshCartUI replaces the whole <cart-drawer> element, which
               throws away the bar we just filled. */
            render();
          });
      } catch (err) {
        state.selfRefresh = false;
        log('DWLCart.refreshCartUI failed', err);
      }
    }
    return fallbackRefresh();
  }

  function fallbackRefresh() {
    var ids = (CONFIG.refreshSections || '').split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
    if (!ids.length) return Promise.resolve();

    return nativeFetch('/?sections=' + encodeURIComponent(ids.join(',')), { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        ids.forEach(function (id) {
          var html = sections[id];
          var target = document.getElementById('shopify-section-' + id);
          if (!html || !target) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var source = parsed.querySelector('#shopify-section-' + id) ||
                       parsed.querySelector('.shopify-section') || parsed.body;
          target.innerHTML = source.innerHTML;
        });
        render();
      })
      .catch(function () { /* the theme will catch up on its own */ });
  }

  /* -----------------------------------------------------------------------
     Measurement
     ----------------------------------------------------------------------- */

  function isGiftLine(item) {
    return !!(item.properties &&
      (item.properties[PROP_GIFT] === 'true' || item.properties[PROP_GIFT] === true));
  }

  function tierById(id) {
    for (var i = 0; i < TIERS.length; i++) {
      if (TIERS[i].id === id) return TIERS[i];
    }
    return null;
  }

  function optionByVariant(tier, variantId) {
    for (var i = 0; i < tier.options.length; i++) {
      if (String(tier.options[i].variantId) === String(variantId)) return tier.options[i];
    }
    return null;
  }

  function cartAttr(cart, key) {
    return (cart && cart.attributes && cart.attributes[key]) || '';
  }

  function inScope(item, tier) {
    switch (tier.scope) {
      case 'collection':
        return (tier.scopeProductIds || []).indexOf(item.product_id) !== -1;
      case 'type':
        return String(item.product_type || '').toLowerCase() ===
               String(tier.scopeValue || '').toLowerCase();
      case 'vendor':
        return String(item.vendor || '').toLowerCase() ===
               String(tier.scopeValue || '').toLowerCase();
      default:
        return true;
    }
  }

  function measure(cart, tier) {
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (isGiftLine(item)) continue;
      if (!inScope(item, tier)) continue;
      total += tier.basis === 'quantity' ? item.quantity : item.final_line_price;
    }
    return total;
  }

  function plainSubtotal(cart) {
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isGiftLine(cart.items[i])) total += cart.items[i].final_line_price;
    }
    return total;
  }

  function inWindow(tier, value) {
    if (value < tier.threshold) return false;
    if (tier.max && tier.max > 0 && value > tier.max) return false;
    return true;
  }

  function activeTiers(cart) {
    var reached = TIERS.filter(function (t) { return inWindow(t, measure(cart, t)); });
    if (!reached.length) return [];
    var result = [];
    for (var i = reached.length - 1; i >= 0; i--) {
      result.unshift(reached[i]);
      if (reached[i].mode === 'replace') break;
    }
    return result;
  }

  /* -----------------------------------------------------------------------
     Desired gifts per tier
     ----------------------------------------------------------------------- */

  function storedChoices(tier, cart) {
    var raw = cartAttr(cart, attrChoice(tier.id));
    if (!raw) return [];
    return raw.split(',').filter(Boolean).filter(function (variantId) {
      var option = optionByVariant(tier, variantId);
      return option && option.available;
    });
  }

  function desiredVariants(tier, cart) {
    if (tier.selection === 'fixed') {
      var first = tier.options[0];
      return first && first.available ? [String(first.variantId)] : [];
    }
    return storedChoices(tier, cart).slice(0, tier.choiceCount || 1);
  }

  function awaitingChoice(tier, cart) {
    if (tier.selection === 'fixed') return false;
    return desiredVariants(tier, cart).length < (tier.choiceCount || 1);
  }

  /* -----------------------------------------------------------------------
     Reconcile
     ----------------------------------------------------------------------- */

  function reconcile() {
    if (state.busy) { state.queued = true; return Promise.resolve(); }
    state.busy = true;

    return getCart()
      .then(function (cart) {
        var active = activeTiers(cart);
        var activeIds = {};
        active.forEach(function (t) { activeIds[t.id] = true; });

        var desired = {};
        active.forEach(function (tier) {
          if (tier.autoAdd === false) return;
          if (cartAttr(cart, attrOff(tier.id)) === '1') return;
          if (awaitingChoice(tier, cart)) return;
          desired[tier.id] = desiredVariants(tier, cart);
        });

        var updates = {};
        var attributes = {};
        var seen = {};
        var hadLine = {};
        var hasUpdates = false;
        var giftLineCount = 0;

        cart.items.filter(isGiftLine).forEach(function (line) {
          var tierId = line.properties[PROP_TIER];
          var tier = tierById(tierId);
          var key = tierId + '|' + line.variant_id;
          var wanted = desired[tierId] || [];
          var isWanted = tier && activeIds[tierId] &&
                         wanted.indexOf(String(line.variant_id)) !== -1;

          /* Recorded before the verdict. A tier whose line we are about to
             swap for a different variant still had a line — treating that as
             a deletion would strand the customer's new choice. */
          hadLine[tierId] = true;

          if (!isWanted || seen[key] || giftLineCount >= CONFIG.maxGiftLines) {
            updates[line.key] = 0;
            hasUpdates = true;
            return;
          }

          seen[key] = true;
          giftLineCount++;

          var wantedQty = tier.giftQuantity || 1;
          if (line.quantity !== wantedQty) {
            updates[line.key] = wantedQty;
            hasUpdates = true;
          }

          if (tier.pricing === 'discount' && line.final_line_price > 0) {
            console.warn(
              '[gift-rewards] gift line is not at 0.00. Is the ' +
              'gift-rewards-discount function deployed and active under Discounts?', line
            );
          }
        });

        cartAttr(cart, ATTR_ACTIVE).split(',').filter(Boolean).forEach(function (tierId) {
          if (!activeIds[tierId]) return;
          if (cartAttr(cart, attrOff(tierId)) === '1') return;
          if (hadLine[tierId]) return;
          if ((desired[tierId] || []).length) {
            attributes[attrOff(tierId)] = '1';
            delete desired[tierId];
          }
        });

        TIERS.forEach(function (tier) {
          if (!activeIds[tier.id] && cartAttr(cart, attrOff(tier.id))) {
            attributes[attrOff(tier.id)] = '';
          }
        });

        var additions = [];
        active.forEach(function (tier) {
          (desired[tier.id] || []).forEach(function (variantId) {
            var key = tier.id + '|' + variantId;
            if (seen[key]) return;
            if (giftLineCount >= CONFIG.maxGiftLines) return;

            var properties = {};
            properties[PROP_GIFT] = 'true';
            properties[PROP_TIER] = tier.id;

            additions.push({
              id: Number(variantId),
              quantity: tier.giftQuantity || 1,
              properties: properties
            });
            seen[key] = true;
            giftLineCount++;
          });
        });

        var expected = Object.keys(seen).map(function (k) { return k.split('|')[0]; });
        var uniqueExpected = expected.filter(function (v, i) {
          return expected.indexOf(v) === i;
        }).join(',');

        if (uniqueExpected !== cartAttr(cart, ATTR_ACTIVE)) {
          attributes[ATTR_ACTIVE] = uniqueExpected;
        }

        var attributeKeys = Object.keys(attributes);
        var chain = Promise.resolve();
        var addResult = null;

        if (hasUpdates || attributeKeys.length) {
          var payload = {};
          if (hasUpdates) payload.updates = updates;
          if (attributeKeys.length) payload.attributes = attributes;
          log('update', payload);
          chain = chain.then(function () { return postJSON('/cart/update.js', payload); });
        }

        if (additions.length) {
          log('add', additions);
          chain = chain.then(function () {
            return postJSON('/cart/add.js', {
              items: additions,
              sections: refreshSectionIds()
            }).then(function (result) {
              if (result && result.status && result.status !== 200) {
                console.warn(
                  '[gift-rewards] gift could not be added. The most common cause is ' +
                  'a gift product that is not published to the Online Store sales channel.',
                  result
                );
                return;
              }
              addResult = result;
            });
          });
        }

        var mutated = hasUpdates || attributeKeys.length > 0 || additions.length > 0;

        return chain
          .then(function () { return mutated ? getCart() : cart; })
          .then(function (finalCart) {
            state.cart = finalCart;
            render();
            if (mutated) {
              syncCartUI(addResult && addResult.sections ? { sections: addResult.sections } : null);
            }
          });
      })
      .catch(function (err) {
        console.warn('[gift-rewards] reconcile failed', err);
      })
      .then(function () {
        state.busy = false;
        if (state.queued) { state.queued = false; schedule(); }
      });
  }

  function schedule() {
    clearTimeout(state.timer);
    state.timer = setTimeout(reconcile, 50);
  }

  /* Paint from a cart we already have, without waiting for a round trip.
     Shopify hands back the full cart on update/change/clear, and the added
     lines on add. Both are enough to move the bar now; reconcile confirms a
     moment later. Waiting for our own /cart.js call before showing anything
     is what made this feel a beat behind the click. */
  function adoptCartPayload(data) {
    if (!data || !data.items) return false;

    if (typeof data.item_count === 'number') {
      state.cart = data;
      render();
      return true;
    }

    /* An add response: line items, no totals. Fold them into the cart we
       already hold so the subtotal moves immediately. */
    if (state.cart && data.items.length) {
      var merged = { items: state.cart.items.slice(), attributes: state.cart.attributes };
      data.items.forEach(function (line) { merged.items.push(line); });
      state.cart = merged;
      render();
      return true;
    }
    return false;
  }

  /* -----------------------------------------------------------------------
     Goals
     ----------------------------------------------------------------------- */

  function goalsFor(cart) {
    var goals = [];

    TIERS.forEach(function (tier) {
      var value = measure(cart, tier);
      goals.push({
        id: tier.id,
        kind: 'tier',
        tier: tier,
        threshold: tier.threshold,
        value: value,
        unlocked: inWindow(tier, value),
        onAxis: tier.basis !== 'quantity' && tier.scope === 'all',
        badge: tier.badge
      });
    });

    if (SHIPPING) {
      var subtotal = plainSubtotal(cart);
      goals.push({
        id: 'shipping',
        kind: 'shipping',
        threshold: SHIPPING.threshold,
        value: subtotal,
        unlocked: subtotal >= SHIPPING.threshold,
        onAxis: true,
        badge: null
      });
    }

    return goals;
  }

  function focusGoal(goals) {
    var open = goals.filter(function (g) { return g.value < g.threshold; });
    if (!open.length) return null;
    return open.sort(function (a, b) {
      return (a.threshold - a.value) - (b.threshold - b.value);
    })[0];
  }

  function goalMessage(goal) {
    if (!goal) return CONFIG.strings.allUnlocked;

    if (goal.kind === 'shipping') {
      return goal.unlocked
        ? SHIPPING.unlockedText
        : interpolate(SHIPPING.lockedText, {
            amount: formatMoney(goal.threshold - goal.value)
          });
    }

    var tier = goal.tier;
    if (goal.unlocked) {
      return interpolate(tier.unlockedText || CONFIG.strings.unlocked, { gift: tier.title });
    }

    var remaining = goal.threshold - goal.value;
    if (tier.basis === 'quantity') {
      return interpolate(tier.lockedText || CONFIG.strings.remainingItems, {
        count: remaining, gift: tier.title
      });
    }
    return interpolate(tier.lockedText || CONFIG.strings.remaining, {
      amount: formatMoney(remaining), gift: tier.title
    });
  }

  function percent(value, threshold) {
    if (threshold <= 0) return 100;
    return Math.max(0, Math.min(100, (value / threshold) * 100));
  }

  /* -----------------------------------------------------------------------
     Suggestions that close the gap
     ----------------------------------------------------------------------- */

  function smallestGap(goals) {
    var gaps = goals
      .filter(function (g) { return g.onAxis && g.value < g.threshold; })
      .map(function (g) { return g.threshold - g.value; });
    return gaps.length ? Math.min.apply(null, gaps) : 0;
  }

  function fillerMatches(cart, goals) {
    if (!FILLER) return [];
    var gap = smallestGap(goals);
    if (gap <= 0) return [];

    var ceiling = gap + (FILLER.overshoot || 0);
    var inCart = {};
    cart.items.forEach(function (item) { inCart[item.variant_id] = true; });

    return FILLER.products
      .filter(function (p) {
        return p.price >= gap && p.price <= ceiling && !inCart[p.variantId];
      })
      .sort(function (a, b) { return a.price - b.price; })
      .slice(0, FILLER.count || 3);
  }

  function addFiller(variantId, button) {
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }
    postJSON('/cart/add.js', {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: refreshSectionIds()
    })
      .then(function (result) {
        return getCart().then(function (cart) {
          state.cart = cart;
          render();
          return syncCartUI(result && result.sections ? { sections: result.sections } : null);
        });
      })
      .then(function () { schedule(); })
      .catch(function (err) { log('adding a suggestion failed', err); })
      .then(function () {
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      });
  }

  /* -----------------------------------------------------------------------
     Rendering
     ----------------------------------------------------------------------- */

  var DUCK_SVG = null;

  var DUCK_FALLBACK =
    '<svg viewBox="0 0 32 26" width="26" height="21" fill="none" focusable="false">' +
      '<path d="M5 18.5c0-3.6 3.2-6.2 7.4-6.2h5.1c1 0 1.8-.5 2.4-1.3l1.6-2.2c.5-.7 1.5-.8 2.1-.2.5.5.6 1.3.2 1.9l-1 1.5c1.9.9 3.2 2.6 3.2 4.7 0 3.2-3 5.6-7.3 5.6h-6.3C7.9 22.3 5 20.7 5 18.5Z" fill="var(--gr-duck-body, #ffd166)"/>' +
      '<circle cx="22.4" cy="7.6" r="5.1" fill="var(--gr-duck-body, #ffd166)"/>' +
      '<path d="M27.2 6.1h3.6c.7 0 1.2.6 1.1 1.2-.1.8-.9 1.4-1.9 1.4h-2.8V6.1Z" fill="var(--gr-duck-bill, #e67d00)"/>' +
      '<circle cx="23.6" cy="6.6" r="1" fill="var(--gr-duck-eye, #0d0d0d)"/>' +
    '</svg>';

  function duckMarkup() {
    if (DUCK_SVG !== null) return DUCK_SVG;
    var painted = document.querySelector('[data-gift-duck] svg');
    if (!painted) {
      var tpl = document.getElementById('GiftRewardsFirstPaint');
      painted = tpl && tpl.content ? tpl.content.querySelector('[data-gift-duck] svg') : null;
    }
    DUCK_SVG = painted ? painted.outerHTML : DUCK_FALLBACK;
    return DUCK_SVG;
  }

  function setText(node, value) {
    if (!node) return;
    var text = String(value == null ? '' : value);
    if (!node.firstChild || node.firstChild.nodeType !== 3) {
      node.textContent = text;
      return;
    }
    if (node.firstChild.nodeValue !== text) node.firstChild.nodeValue = text;
    while (node.childNodes.length > 1) node.removeChild(node.lastChild);
  }

  function ensureSkeleton(container) {
    if (container.querySelector('[data-gift-track]')) return;

    var inner = document.createElement('div');
    inner.className = 'gift-rewards__inner';
    inner.innerHTML =
      '<p class="gift-rewards__message" data-gift-message>' +
        '<span class="gift-rewards__amount" data-gift-amount></span>' +
        '<span class="gift-rewards__text" data-gift-text></span>' +
      '</p>' +
      '<div class="gift-rewards__track" data-gift-track>' +
        '<div class="gift-rewards__water" data-gift-water></div>' +
        '<div class="gift-rewards__duck" data-gift-duck aria-hidden="true">' + duckMarkup() + '</div>' +
        '<div class="gift-rewards__markers" data-gift-markers></div>' +
      '</div>';

    container.innerHTML = '';
    container.appendChild(inner);
    container.setAttribute('data-gift-painted', 'client');
  }

  function syncMarkers(container, goals, highest) {
    var host = container.querySelector('[data-gift-markers]');
    if (!host) return;

    var axis = goals.filter(function (g) { return g.onAxis && g.threshold > 0; });
    var signature = axis.map(function (g) { return g.id + ':' + g.threshold; }).join('|');

    if (host.getAttribute('data-gift-signature') !== signature) {
      host.innerHTML = axis.map(function (g) {
        var at = highest > 0 ? (g.threshold / highest) * 100 : 100;
        var label = g.kind === 'shipping' ? 'free shipping' : (g.tier && g.tier.title) || '';
        return '<span class="gift-rewards__marker' +
            (g.kind === 'shipping' ? ' gift-rewards__marker--shipping' : '') + '"' +
            ' data-gift-marker="' + escapeHtml(g.id) + '"' +
            ' style="--gr-at: ' + at.toFixed(2) + '%"' +
            ' title="' + escapeHtml(label) + '">' +
            (g.badge
              ? '<img src="' + escapeHtml(g.badge) + '" alt="" width="20" height="20" loading="eager">'
              : '<span class="gift-rewards__marker-dot"></span>') +
          '</span>';
      }).join('');
      host.setAttribute('data-gift-signature', signature);
    }

    axis.forEach(function (g) {
      var el = host.querySelector('[data-gift-marker="' + g.id + '"]');
      if (el) el.classList.toggle('is-unlocked', g.unlocked);
    });
  }

  function syncAction(container, cart) {
    var inner = container.querySelector('.gift-rewards__inner');
    if (!inner) return;

    var candidate = null;
    activeTiers(cart).forEach(function (tier) {
      if (candidate) return;
      var dismissed = cartAttr(cart, attrOff(tier.id)) === '1';
      if (tier.selection !== 'fixed') {
        candidate = {
          tier: tier,
          action: 'open',
          label: (awaitingChoice(tier, cart) || dismissed)
            ? CONFIG.strings.choose
            : CONFIG.strings.change
        };
      } else if (tier.autoAdd === false || dismissed) {
        candidate = { tier: tier, action: 'add', label: CONFIG.strings.add };
      }
    });

    var button = inner.querySelector('[data-gift-action]');

    if (!candidate) {
      if (button) button.hidden = true;
      return;
    }

    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'gift-rewards__action';
      button.setAttribute('data-gift-action', '');
      inner.appendChild(button);
    }

    button.hidden = false;
    button.setAttribute('data-gift-action', candidate.action);
    button.setAttribute('data-gift-tier', candidate.tier.id);
    setText(button, candidate.label);
  }

  function syncFiller(container, cart, goals) {
    var inner = container.querySelector('.gift-rewards__inner');
    if (!inner) return;

    var matches = FILLER ? fillerMatches(cart, goals) : [];
    var host = inner.querySelector('[data-gift-filler]');
    var signature = matches.map(function (p) { return p.variantId; }).join(',');

    if (!matches.length) {
      if (host) host.remove();
      return;
    }

    if (!host) {
      host = document.createElement('div');
      host.className = 'gift-rewards__filler';
      host.setAttribute('data-gift-filler', '');
      inner.appendChild(host);
    }

    if (host.getAttribute('data-gift-signature') === signature) return;

    host.innerHTML =
      '<p class="gift-rewards__filler-heading">' + escapeHtml(FILLER.heading) + '</p>' +
      '<ul class="gift-rewards__filler-list">' +
        matches.map(function (p) {
          return '<li class="gift-rewards__filler-item">' +
            '<button type="button" class="gift-rewards__filler-button" ' +
              'data-gift-filler-add="' + p.variantId + '">' +
              (p.image
                ? '<img src="' + escapeHtml(p.image) + '" alt="" loading="lazy" width="56" height="56">'
                : '<span class="gift-rewards__filler-placeholder"></span>') +
              '<span class="gift-rewards__filler-title">' + escapeHtml(p.title) + '</span>' +
              '<span class="gift-rewards__filler-price">' + escapeHtml(formatMoney(p.price)) + '</span>' +
            '</button>' +
          '</li>';
        }).join('') +
      '</ul>';
    host.setAttribute('data-gift-signature', signature);
  }

  function render() {
    var containers = document.querySelectorAll('[data-gift-rewards]');

    var cart = state.cart;
    if (!cart) return;

    var goals = goalsFor(cart);
    var focus = focusGoal(goals);

    var message;
    if (focus) {
      message = goalMessage(focus);
    } else {
      var reached = goals
        .filter(function (g) { return g.unlocked && g.kind === 'tier'; })
        .sort(function (a, b) { return a.threshold - b.threshold; });
      message = reached.length
        ? goalMessage(reached[reached.length - 1])
        : goalMessage(null);
    }

    /* Split the amount off the front of the message so it can carry its own
       weight and colour. Only when the copy actually starts with it — move
       the placeholder into the middle of the sentence and the split would
       reorder the words, so in that case the message stays one piece. The
       leading space stays on the tail, which keeps textContent identical to
       the unsplit string. */
    var amountText = '';
    if (focus) {
      amountText = focus.tier && focus.tier.basis === 'quantity'
        ? String(focus.threshold - focus.value)
        : formatMoney(focus.threshold - focus.value);
    }
    var head = '';
    var tail = message;
    if (amountText && message.indexOf(amountText) === 0) {
      head = amountText;
      tail = message.slice(amountText.length);
    }

    var axis = goals.filter(function (g) { return g.onAxis && g.threshold > 0; });
    var highest = axis.reduce(function (max, g) {
      return g.threshold > max ? g.threshold : max;
    }, 0);

    var compactPct = focus ? percent(focus.value, focus.threshold) : 100;
    var fullPct = highest > 0 ? percent(plainSubtotal(cart), highest) : 0;

    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      ensureSkeleton(container);

      var compact = container.getAttribute('data-variant') === 'compact';
      var pct = compact ? compactPct : fullPct;

      var inner = container.querySelector('.gift-rewards__inner');
      var track = container.querySelector('[data-gift-track]');

      var amountNode = container.querySelector('[data-gift-amount]');
      var textNode = container.querySelector('[data-gift-text]');
      if (amountNode && textNode) {
        setText(amountNode, head);
        setText(textNode, tail);
      } else {
        /* Server paint from a template that predates the split. */
        setText(container.querySelector('[data-gift-message]'), message);
      }

      if (track) {
        track.style.setProperty('--gr-progress', pct.toFixed(2) + '%');
        track.setAttribute('role', 'progressbar');
        track.setAttribute('aria-valuemin', '0');
        track.setAttribute('aria-valuemax', '100');
        track.setAttribute('aria-valuenow', String(Math.round(pct)));
      }

      if (inner) {
        /* The duck goes when there is nothing left to swim towards. */
        inner.classList.toggle('is-unlocked', !focus);
      }

      if (!compact) syncMarkers(container, goals, highest);

      syncAction(container, cart);
      syncFiller(container, cart, goals);

      container.hidden = false;
    }

    /* Publish the state on <html> so anything outside the bar can key off it
       without the engine needing to know that thing exists. */
    var root = document.documentElement;
    root.style.setProperty('--gr-cart-progress', compactPct.toFixed(2) + '%');
    root.style.setProperty('--gr-cart-remaining', '"' + amountText.replace(/"/g, '') + '"');
    root.setAttribute('data-gift-state', focus ? 'locked' : 'unlocked');
  }

  /* -----------------------------------------------------------------------
     Gift picker
     ----------------------------------------------------------------------- */

  var picker = null;
  var pickerTier = null;
  var pickerSelection = [];
  var pickerReturnFocus = null;

  function buildPicker() {
    if (picker) return picker;

    picker = document.createElement('div');
    picker.className = 'gift-rewards-picker';
    picker.hidden = true;
    picker.setAttribute('role', 'dialog');
    picker.setAttribute('aria-modal', 'true');
    picker.innerHTML =
      '<div class="gift-rewards-picker__backdrop" data-picker-close></div>' +
      '<div class="gift-rewards-picker__panel">' +
        '<header class="gift-rewards-picker__header">' +
          '<h2 class="gift-rewards-picker__title"></h2>' +
          '<button type="button" class="gift-rewards-picker__close" data-picker-close ' +
            'aria-label="' + escapeHtml(CONFIG.strings.close) + '">&times;</button>' +
        '</header>' +
        '<div class="gift-rewards-picker__grid"></div>' +
        '<footer class="gift-rewards-picker__footer" hidden>' +
          '<span class="gift-rewards-picker__counter"></span>' +
          '<button type="button" class="gift-rewards-picker__confirm" data-picker-confirm></button>' +
        '</footer>' +
      '</div>';

    document.body.appendChild(picker);

    picker.addEventListener('click', function (event) {
      if (event.target.closest('[data-picker-close]')) closePicker();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && picker && !picker.hidden) closePicker();
    });

    return picker;
  }

  function renderPickerState() {
    if (!picker || !pickerTier) return;
    var multi = (pickerTier.choiceCount || 1) > 1;

    picker.querySelectorAll('.gift-rewards-picker__option').forEach(function (button) {
      button.classList.toggle(
        'is-selected',
        pickerSelection.indexOf(button.getAttribute('data-variant')) !== -1
      );
    });

    var footer = picker.querySelector('.gift-rewards-picker__footer');
    footer.hidden = !multi;
    if (multi) {
      picker.querySelector('.gift-rewards-picker__counter').textContent =
        interpolate(CONFIG.strings.pickerCounter, {
          selected: pickerSelection.length, total: pickerTier.choiceCount
        });
      var confirm = picker.querySelector('.gift-rewards-picker__confirm');
      confirm.textContent = CONFIG.strings.pickerConfirm;
      confirm.disabled = pickerSelection.length !== pickerTier.choiceCount;
    }
  }

  function openPicker(tierId) {
    var tier = tierById(tierId);
    if (!tier || !state.cart) return;

    pickerTier = tier;
    pickerSelection = desiredVariants(tier, state.cart).slice();
    pickerReturnFocus = document.activeElement;

    var el = buildPicker();
    el.querySelector('.gift-rewards-picker__title').textContent = CONFIG.strings.pickerTitle;
    el.querySelector('.gift-rewards-picker__grid').innerHTML = tier.options.map(function (option) {
      var value = option.compareAt > option.price ? option.compareAt : option.price;
      return '<button type="button" class="gift-rewards-picker__option' +
          (option.available ? '' : ' is-unavailable') + '"' +
          (option.available ? '' : ' disabled') +
          ' data-variant="' + option.variantId + '">' +
          (option.image
            ? '<img src="' + escapeHtml(option.image) + '" alt="" loading="lazy">'
            : '<span class="gift-rewards-picker__placeholder"></span>') +
          '<span class="gift-rewards-picker__name">' + escapeHtml(option.title) + '</span>' +
          (option.subtitle
            ? '<span class="gift-rewards-picker__variant">' + escapeHtml(option.subtitle) + '</span>'
            : '') +
          (option.available
            ? (value > 0
                ? '<span class="gift-rewards-picker__value">' +
                    escapeHtml(interpolate(CONFIG.strings.value, { amount: formatMoney(value) })) +
                  '</span>'
                : '')
            : '<span class="gift-rewards-picker__sold-out">' +
                escapeHtml(CONFIG.strings.soldOut) + '</span>') +
        '</button>';
    }).join('');

    el.hidden = false;
    document.body.classList.add('gift-rewards-picker-open');
    renderPickerState();

    var first = el.querySelector('.gift-rewards-picker__option:not([disabled])');
    if (first) first.focus();
  }

  function closePicker() {
    if (!picker) return;
    picker.hidden = true;
    pickerTier = null;
    document.body.classList.remove('gift-rewards-picker-open');
    if (pickerReturnFocus && pickerReturnFocus.focus) {
      try { pickerReturnFocus.focus(); } catch (err) { /* element gone */ }
    }
    pickerReturnFocus = null;
  }

  function commitChoice(tier, variantIds) {
    var attributes = {};
    attributes[attrChoice(tier.id)] = variantIds.join(',');
    attributes[attrOff(tier.id)] = '';

    closePicker();
    state.busy = true;

    return postJSON('/cart/update.js', { attributes: attributes })
      .then(function () { state.busy = false; return reconcile(); })
      .catch(function () { state.busy = false; });
  }

  /* -----------------------------------------------------------------------
     Events
     ----------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var action = event.target.closest('[data-gift-action]');
    if (action) {
      event.preventDefault();
      var tier = tierById(action.getAttribute('data-gift-tier'));
      if (!tier) return;
      if (action.getAttribute('data-gift-action') === 'open') {
        openPicker(tier.id);
      } else {
        commitChoice(tier, [String(tier.options[0].variantId)]);
      }
      return;
    }

    var filler = event.target.closest('[data-gift-filler-add]');
    if (filler) {
      event.preventDefault();
      addFiller(filler.getAttribute('data-gift-filler-add'), filler);
      return;
    }

    var option = event.target.closest('.gift-rewards-picker__option');
    if (option && !option.disabled && pickerTier) {
      event.preventDefault();
      var variantId = option.getAttribute('data-variant');
      var multi = (pickerTier.choiceCount || 1) > 1;

      if (!multi) { commitChoice(pickerTier, [variantId]); return; }

      var index = pickerSelection.indexOf(variantId);
      if (index !== -1) pickerSelection.splice(index, 1);
      else if (pickerSelection.length < pickerTier.choiceCount) pickerSelection.push(variantId);
      renderPickerState();
      return;
    }

    if (event.target.closest('[data-picker-confirm]') && pickerTier) {
      event.preventDefault();
      commitChoice(pickerTier, pickerSelection.slice());
    }
  });

  /* -----------------------------------------------------------------------
     Change detection
     ----------------------------------------------------------------------- */

  function wrapDWLCart() {
    var d = window.DWLCart;
    if (!d || typeof d.refreshCartUI !== 'function' || d.__giftRewardsWrapped) return false;

    var original = d.refreshCartUI;
    d.refreshCartUI = function () {
      var result = original.apply(this, arguments);
      Promise.resolve(result).then(function () {
        if (state.selfRefresh) return;
        if (state.busy) state.queued = true; else schedule();
      }).catch(function () {});
      return result;
    };
    d.__giftRewardsWrapped = true;
    log('bridged to DWLCart');
    return true;
  }

  var observedFetch = window.fetch;
  window.fetch = function () {
    var request = arguments[0];
    var url = typeof request === 'string' ? request : (request && request.url) || '';
    var promise = observedFetch.apply(this, arguments);
    if (FOREIGN_MUTATION.test(url)) {
      promise.then(function (response) {
        try {
          response.clone().json()
            .then(function (data) { adoptCartPayload(data); })
            .catch(function () {});
        } catch (err) { /* opaque or already consumed */ }
        if (state.busy) state.queued = true; else schedule();
      }).catch(function () {});
    }
    return promise;
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__giftRewardsUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (FOREIGN_MUTATION.test(this.__giftRewardsUrl || '')) {
      this.addEventListener('loadend', function () {
        if (state.busy) state.queued = true; else schedule();
      });
    }
    return nativeSend.apply(this, arguments);
  };

  /* cart-bundle.js replaces the whole <cart-drawer> element from its own
     private refreshCartUI — on quick-add, on bundle removal, on every add
     through DWLCart.add. That call is invisible to us, and it lands AFTER our
     render, so the fresh drawer comes back holding the empty container from
     the snippet. Watching the section wrapper for a swapped child is the only
     signal we get. childList on one wrapper, not the document, and it only
     ever calls render() — no mutations of our own, so no feedback loop. */
  function watchCartContainers() {
    var targets = [];
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.parentNode) targets.push(drawer.parentNode);
    var main = document.querySelector('.main-cart');
    var mainWrap = main && main.closest('.shopify-section');
    if (mainWrap) targets.push(mainWrap);
    if (!targets.length) return;

    var pending;
    var observer = new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () { render(); }, 0);
    });
    targets.forEach(function (t) { observer.observe(t, { childList: true }); });
  }

  document.addEventListener('gift-rewards:refresh', function () { schedule(); });
  window.addEventListener('pageshow', function (event) { if (event.persisted) schedule(); });

  /* -----------------------------------------------------------------------
     Boot
     ----------------------------------------------------------------------- */

  function boot() {
    if (nativeFetchWasTainted) {
      console.warn(
        '[gift-rewards] cart-bundle.js loaded before gift-rewards.js. Move the ' +
        'gift-rewards.js script tag above it. Falling back to an isolated fetch.'
      );
      reacquireFetch();
    }

    if (!wrapDWLCart()) {
      var tries = 0;
      var timer = setInterval(function () {
        if (wrapDWLCart() || ++tries > 40) clearInterval(timer);
      }, 100);
    }

    watchCartContainers();
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.GiftRewards = {
    reconcile: reconcile,
    render: render,
    openPicker: openPicker,
    getState: function () { return state; },
    tiers: TIERS,
    goals: function () { return state.cart ? goalsFor(state.cart) : []; },
    measure: function (tierId) {
      var tier = tierById(tierId);
      return tier && state.cart ? measure(state.cart, tier) : null;
    }
  };
})();
